import { CamelCatalogService, CatalogKind, ICamelComponentDefinition } from '@kaoto/kaoto';

/**
 * Telling Kaoto about the components this platform's runtime carries.
 *
 * The data plane is a Camel build with three components of its own on its classpath. Kaoto builds
 * a node's form from the catalog, and the catalog only describes what Camel ships — so without
 * this, clicking any of our nodes says "this node cannot be configured yet" and the form that
 * belongs there never appears.
 *
 * Only what a person configures is described. The runtime reads the rest — a contract, an alert
 * webhook — from the revision it is running, precisely so those things never have to live in the
 * flow, where they would be copied along with it.
 */

const describe = (
  name: string,
  title: string,
  description: string,
  properties: ICamelComponentDefinition['properties'] = {},
): ICamelComponentDefinition => ({
  component: {
    kind: CatalogKind.Component,
    name,
    title,
    description,
    deprecated: false,
    supportLevel: 'Stable',
    provider: 'Custom',
    label: 'platform',
    version: '1.0.0',
    scheme: name,
    syntax: `${name}:name`,
    producerOnly: true,
  },
  componentProperties: {},
  properties,
  propertiesSchema: {
    type: 'object',
    properties: Object.fromEntries(
      Object.entries(properties).map(([key, property]) => [
        key,
        {
          type: property.type === 'integer' ? 'integer' : 'string',
          title: property.displayName,
          description: property.description,
        },
      ]),
    ),
  },
});

const property = (displayName: string, description: string, type = 'string') =>
  ({
    index: 0,
    kind: 'parameter',
    displayName,
    group: 'producer',
    label: '',
    required: false,
    type,
    javaType: 'java.lang.String',
    deprecated: false,
    autowired: false,
    secret: false,
    description,
  }) as ICamelComponentDefinition['properties'][string];

export const registerPlatformComponents = (): void => {
  CamelCatalogService.addContributed(CatalogKind.Component, {
    'platform-contract': describe(
      'platform-contract',
      'Validate vendor payload',
      'Holds this vendor to the shape you agreed with them. The rules are configured on this node and travel with the revision, so the same flow can hold a vendor, a tenant and one business to different rules.',
    ),
    'platform-reshape': describe(
      'platform-reshape',
      'Reshape the document',
      'Leaves items out, moves items to another business unit as a document of their own, and counts the totals again. Add this node only for a tenant who asked for it; the rules are configured on the node and travel with the revision.',
    ),
    'platform-wrap': describe(
      'platform-wrap',
      'Wrap the message',
      'Puts each document inside the message its consumer expects: fixed values, fields of the document, tenant details, the time, a new id and the document itself. Rules can change a field, such as an older bill going out as a different event. Configured on the node, per tenant if needed.',
    ),
    'platform-identity': describe(
      'platform-identity',
      'Add the tenant and business',
      'Adds who this message belongs to, after the mapping. A vendor never sends a tenant id, so it is put on here rather than mapped from their payload.',
      {
        at: property(
          'Where to put it',
          'The field the identity is written under. `*` spreads it across the top level.',
        ),
        include: property('Which fields', 'Comma separated: tenantId, tenantName, businessId, businessName, vendorId.'),
      },
    ),
    'platform-field': describe(
      'platform-field',
      'Read a field',
      'Reads one field of the bill, such as invoiceType, so the next choice can send each kind of bill its own way. The field is named after the colon (orderItems[].status for a list); the bill itself is left unchanged.',
      {
        as: property(
          'Read into',
          'The header the value is read into, for the choice to test. Left empty, it is the field name.',
        ),
      },
    ),
    'platform-alert': describe(
      'platform-alert',
      'Tell the team',
      'Raises an alert for a message that could not be delivered. Where it goes, and whether it goes at all, is a setting rather than part of the flow.',
      { kind: property('Kind of failure', 'undelivered, unprocessable, version-refused or ingress-failed.') },
    ),
  });
};
